import { QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import _ from 'lodash';
import { generatePath } from 'react-router-dom';
import { SelectOption } from '../../../components/controlled-autocomplete-input/types';
import { API_URLS } from '../../../routes/urls';
import { StrOrNum } from '../../../types/util';
import { convertObjectToFormData } from '../../../utils/form';
import { apiSlice } from '../../apiSlice';
import { BaseQueryError, QueryErrorReturnValue } from '../../types';
import { uploadToS3RtkApi } from '../s3-upload/util';
import { addSnackbar } from '../snackbar/snackbarSlice';
import { GetProfileResponse } from '../user/types';
import { userApi } from '../user/userSlice';
import {
  EducationFormOptionsResponse,
  BaseEducationFormProgramOptionsResponse,
  BaseEducationFormOptionsResponse,
  EducationFormOptionsByProgramResponse,
  BaseEducationFormOptionsByProgramResponse,
  UpdateEducationPayload,
} from './types';

export const educationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Static Form options for education form
     */
    fetchEducationFormOptions: builder.query<EducationFormOptionsResponse, void>({
      query: () => ({
        url: API_URLS.educationFormOptions,
      }),
      transformResponse({ ranks, institute, passoutYear }: BaseEducationFormOptionsResponse) {
        return {
          ranks: _.keys(ranks).map((rank) => ({ label: ranks[rank], value: rank })),
          institute: institute.map((instituteItem) => ({
            label: instituteItem.name,
            value: instituteItem.id,
            helperText: instituteItem.address,
          })),
          passoutYear: _.values(passoutYear).map((year) => ({ label: year.toString(), value: year.toString() })),
        };
      },
    }),

    /**
     * Static Program Options for education form
     */
    fetchEducationFormProgramOptions: builder.query<SelectOption[], void>({
      query: () => ({
        url: API_URLS.educationFormProgramOptions,
      }),
      transformResponse(programs: BaseEducationFormProgramOptionsResponse) {
        return programs.map((program) => ({
          label: program.name,
          value: program.id,
          helperText: `Affiliated to ${program.affiliation.name}${
            program.affiliation.address ? ', ' + program.affiliation.address : ''
          }`,
        }));
      },
    }),

    /**
     * Options based on program selected
     */
    fetchEducationFormOptionsByProgram: builder.query<EducationFormOptionsByProgramResponse, StrOrNum>({
      query: (programId) => ({
        url: API_URLS.educationFormOptionsByProgram,
        params: { program: programId },
      }),
      transformResponse({ data: { course, major, submajor } }: BaseEducationFormOptionsByProgramResponse) {
        return {
          course: course.map((courseItem) => ({
            label: `${courseItem.short_code ? courseItem.short_code + ' ' : ''}${courseItem.title}`,
            value: courseItem.id,
          })),
          major: major.map((majorItem) => ({ label: majorItem.name, value: majorItem.id })),
          submajor: submajor.map((submajorItem) => ({ label: submajorItem.name, value: submajorItem.id })),
        };
      },
    }),

    /**
     * Update education of student
     */
    updateEducation: builder.mutation<GetProfileResponse, UpdateEducationPayload>({
      async queryFn(
        { email, slug, marksheet, transcript, character_certificate, ...otherInputs },
        _baseQuery,
        _extraOptions,
        api
      ) {
        const uploadDocumentResponses = await uploadToS3RtkApi({
          files: [...marksheet, ...transcript, ...character_certificate],
          module: 'Student',
          user: email,
        });
        if (uploadDocumentResponses.data) {
          const uploadedMarksheets = uploadDocumentResponses.data.slice(0, marksheet.length);
          const uploadedTranscripts = uploadDocumentResponses.data.slice(
            marksheet.length,
            marksheet.length + transcript.length
          );
          const uploadedCharacterCertificates = uploadDocumentResponses.data.slice(
            marksheet.length + transcript.length
          );
          return api({
            url: generatePath(API_URLS.updateEducation, { slug }),
            method: 'POST',
            data: convertObjectToFormData({
              ...otherInputs,
              marksheet: uploadedMarksheets,
              transcript: uploadedTranscripts,
              character_certificate: uploadedCharacterCertificates,
            }),
          }) as QueryReturnValue<GetProfileResponse, BaseQueryError>;
        } else {
          return uploadDocumentResponses;
        }
      },
      async onQueryStarted({ user_id, slug }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            userApi.util.updateQueryData('getProfile', { slug, userId: user_id }, (draft) => {
              draft.user = data.student;
            })
          );
          dispatch(
            addSnackbar({
              severity: 'success',
              message: 'Your education information has been updated successfully',
            })
          );
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          if (err.error.status !== 422) {
            dispatch(
              addSnackbar({
                message: err.error.message,
                severity: 'error',
              })
            );
          }
        }
      },
      invalidatesTags: ['EligibleModule'],
    }),
  }),
});

export const {
  useFetchEducationFormOptionsQuery,
  useFetchEducationFormProgramOptionsQuery,
  useFetchEducationFormOptionsByProgramQuery,
  useUpdateEducationMutation,
} = educationApi;
